import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Calendar,
  CalendarCheck,
  Check,
  ChevronDown,
  ChevronRight,
  Clock,
  Copy,
  ExternalLink,
  Plus,
  RefreshCw,
  Zap,
} from 'lucide-react';
import { api } from '../../lib/trpc.js';
import { keys } from '../../lib/query.js';
import { Section, SkeletonRows } from '../../components/primitives.js';
import { ErrorText, ErrorView } from '../../components/error-view.js';
import { useStepUp } from './use-step-up.js';

/**
 * The personal calendar feed — per-card opt-in calendar sync.
 *
 * The URL is a bearer credential, shown exactly once, then masked.
 * "Generate" and "Regenerate" are the SAME mutation — `auth.calendarFeed
 * .mint` always mints a fresh URL, revoking whatever was active before.
 */

/* ── Calendar app instructions ─────────────────────────────────────── */

interface CalendarApp {
  readonly name: string;
  readonly icon: string;
  readonly steps: readonly string[];
}

const CALENDAR_APPS: readonly CalendarApp[] = [
  {
    name: 'Google Calendar',
    icon: '📅',
    steps: [
      'Open Google Calendar in your browser',
      'In the left sidebar, find "Other calendars"',
      'Click the + icon → "From URL"',
      'Paste your feed link → "Add calendar"',
    ],
  },
  {
    name: 'Outlook',
    icon: '📆',
    steps: [
      'Open Outlook Calendar in your browser',
      'Click "Add calendar" in the sidebar',
      'Select "Subscribe from web"',
      'Paste your feed link → "Import"',
    ],
  },
  {
    name: 'Apple Calendar (Mac)',
    icon: '🍎',
    steps: [
      'Open the Calendar app',
      'Go to File → New Calendar Subscription…',
      'Paste your feed link → Subscribe',
      'Set auto-refresh to "every hour"',
    ],
  },
  {
    name: 'Apple Calendar (iPhone/iPad)',
    icon: '📱',
    steps: [
      'Go to Settings → Calendar → Accounts',
      'Tap "Add Account" → "Other"',
      'Tap "Add Subscribed Calendar"',
      'Paste your feed link → Save',
    ],
  },
];

/* ── Mini calendar preview (decorative, matches real calendar style) ── */

function MiniCalendarPreview() {
  const today = new Date();
  const dayOfMonth = today.getDate();
  const monthName = today.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  // Generate a realistic-looking week with sample cards
  const weekDays: {
    day: string;
    dayShort: string;
    date: number;
    cards: { ref: string; title: string; color: string }[];
  }[] = [
    { day: 'Monday', dayShort: 'Mon', date: dayOfMonth - ((today.getDay() + 6) % 7), cards: [] },
    { day: 'Tuesday', dayShort: 'Tue', date: dayOfMonth - ((today.getDay() + 5) % 7), cards: [] },
    { day: 'Wednesday', dayShort: 'Wed', date: dayOfMonth - ((today.getDay() + 4) % 7), cards: [] },
    { day: 'Thursday', dayShort: 'Thu', date: dayOfMonth - ((today.getDay() + 3) % 7), cards: [] },
    { day: 'Friday', dayShort: 'Fri', date: dayOfMonth - ((today.getDay() + 2) % 7), cards: [] },
    { day: 'Saturday', dayShort: 'Sat', date: dayOfMonth - ((today.getDay() + 1) % 7), cards: [] },
    { day: 'Sunday', dayShort: 'Sun', date: dayOfMonth - (today.getDay() % 7), cards: [] },
  ];

  // Add sample cards to show what synced cards look like
  const sampleCards: { ref: string; title: string; color: string; dayOffset: number }[] = [
    { ref: 'WEB-142', title: 'Fix login redirect', color: 'bg-priority-urgent', dayOffset: 1 },
    { ref: 'WEB-138', title: 'Design system audit', color: 'bg-priority-medium', dayOffset: 3 },
    { ref: 'WEB-145', title: 'API rate limiting', color: 'bg-warning', dayOffset: 5 },
  ];

  for (const card of sampleCards) {
    const targetDay = weekDays.find((d) => d.date === dayOfMonth + card.dayOffset);
    if (targetDay) {
      targetDay.cards.push(card);
    }
  }

  return (
    <div className="overflow-hidden rounded-xl border border-line/40 bg-surface-sunken/30">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-line/30 px-3 py-2 sm:px-4 sm:py-2.5">
        <div className="flex items-center gap-2">
          <Calendar className="size-4 text-accent" strokeWidth={2} />
          <span className="text-xs font-medium text-ink sm:text-sm">{monthName}</span>
        </div>
        <span className="rounded px-2 py-0.5 text-[10px] font-medium text-accent bg-accent/10 sm:text-[11px]">
          Today
        </span>
      </div>

      {/* Week header — hidden on very small screens */}
      <div className="hidden border-b border-line/30 sm:grid sm:grid-cols-7">
        {weekDays.map((d) => (
          <div
            key={d.day}
            className={`py-1.5 text-center text-[10px] font-medium ${
              d.date === dayOfMonth ? 'text-accent' : 'text-ink-faint'
            }`}
          >
            {d.dayShort}
          </div>
        ))}
      </div>

      {/* Day cells */}
      <div className="grid min-h-[60px] grid-cols-7 sm:min-h-[80px]">
        {weekDays.map((d) => (
          <div
            key={d.day}
            className={`border-r border-line/20 p-0.5 sm:p-1 ${
              d.date === dayOfMonth
                ? 'bg-accent/5'
                : d.cards.length > 0
                  ? 'bg-surface-sunken/20'
                  : ''
            }`}
          >
            <div
              className={`mb-0.5 text-center text-[10px] sm:mb-1 sm:text-[11px] ${
                d.date === dayOfMonth ? 'font-bold text-accent' : 'text-ink-faint'
              }`}
            >
              {d.date}
            </div>
            <div className="space-y-0">
              {d.cards.map((card) => (
                <div
                  key={card.ref}
                  className="group hidden items-center gap-1 rounded bg-surface-hover/50 px-0.5 py-0 sm:flex sm:px-1"
                >
                  <span className={`size-1.5 shrink-0 rounded-full ${card.color}`} />
                  <span className="truncate text-[9px] text-ink-muted">
                    <span className="font-mono text-ink-faint">{card.ref}</span>{' '}
                    <span className="hidden lg:inline">{card.title}</span>
                  </span>
                </div>
              ))}
              {/* Mobile: just show dots */}
              {d.cards.length > 0 && (
                <div className="flex flex-wrap justify-center gap-0.5 sm:hidden">
                  {d.cards.map((card) => (
                    <span
                      key={card.ref}
                      className={`size-1.5 rounded-full ${card.color}`}
                      title={`${card.ref} ${card.title}`}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Footer hint */}
      <div className="flex items-center gap-2 border-t border-line/30 bg-surface-sunken/50 px-3 py-1.5 sm:px-4 sm:py-2">
        <span className="size-1.5 rounded-full bg-priority-urgent" />
        <span className="text-[9px] text-ink-faint sm:text-[10px]">
          Synced cards appear here with their reference and priority
        </span>
      </div>
    </div>
  );
}

/* ── How it works steps ────────────────────────────────────────────── */

function HowItWorks() {
  const steps = [
    {
      icon: <Calendar className="size-4" />,
      title: 'Generate feed URL',
      desc: 'Create a unique subscription link for your calendar',
    },
    {
      icon: <ExternalLink className="size-4" />,
      title: 'Add to your calendar',
      desc: 'Paste the link into Google, Outlook, or Apple Calendar',
    },
    {
      icon: <Zap className="size-4" />,
      title: 'Sync cards',
      desc: 'Click the calendar icon on any card with a due date',
    },
  ];

  return (
    <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
      {steps.map((step, i) => (
        <div
          key={i}
          className="flex flex-col rounded-lg border border-line/30 bg-surface-sunken/20 p-3 sm:items-center sm:text-center"
        >
          <div className="flex items-center gap-2.5 sm:flex-col sm:gap-0">
            <div className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-accent/10 text-accent sm:mb-2 sm:size-10">
              {step.icon}
            </div>
            <div className="sm:space-y-0.5">
              <span className="text-[10px] font-medium uppercase tracking-wider text-ink-faint">
                Step {i + 1}
              </span>
              <p className="text-[12px] font-medium text-ink sm:text-[13px]">{step.title}</p>
            </div>
          </div>
          <p className="mt-1.5 text-[10px] leading-relaxed text-ink-faint sm:mt-2 sm:text-[11px]">
            {step.desc}
          </p>
        </div>
      ))}
    </div>
  );
}

/* ── Status indicator ──────────────────────────────────────────────── */

function FeedStatus({ active }: { readonly active: boolean }) {
  return (
    <div
      className={`flex items-center gap-3 rounded-xl border px-3 py-3 sm:px-4 ${
        active
          ? 'border-accent/30 bg-gradient-to-r from-accent/5 to-transparent'
          : 'border-line/40 bg-surface-sunken/30'
      }`}
    >
      <div
        className={`flex size-10 shrink-0 items-center justify-center rounded-xl ${
          active ? 'bg-accent/15 text-accent' : 'bg-surface-hover text-ink-faint'
        }`}
      >
        {active ? (
          <CalendarCheck className="size-5" strokeWidth={2} />
        ) : (
          <Calendar className="size-5" strokeWidth={1.5} />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="text-[12px] font-semibold text-ink sm:text-[13px]">
            {active ? 'Calendar feed active' : 'Set up calendar sync'}
          </span>
          {active && (
            <span className="relative flex size-2">
              <span className="absolute inline-flex size-full animate-ping rounded-full bg-accent/40" />
              <span className="relative inline-flex size-2 rounded-full bg-accent" />
            </span>
          )}
        </div>
        <p className="text-[10px] text-ink-faint sm:text-[11px]">
          {active
            ? 'Your calendar app is subscribed and receiving updates'
            : 'Generate a URL to see your synced cards in any calendar app'}
        </p>
      </div>
    </div>
  );
}

/* ── URL reveal panel ──────────────────────────────────────────────── */

function UrlReveal({
  url,
  onCopy,
  copied,
}: {
  readonly url: string;
  readonly onCopy: () => void;
  readonly copied: boolean;
}) {
  const [expandedApp, setExpandedApp] = useState<string | null>(null);

  return (
    <div className="space-y-4">
      {/* Success state */}
      <div className="flex items-center gap-3 rounded-xl border border-success/30 bg-success/5 px-3 py-3 sm:px-4">
        <div className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-success/15 text-success">
          <Check className="size-5" strokeWidth={2.5} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[12px] font-semibold text-ink sm:text-[13px]">Feed URL ready</p>
          <p className="text-[10px] text-ink-faint sm:text-[11px]">
            Copy this link — it&apos;s shown only this once
          </p>
        </div>
      </div>

      {/* URL box */}
      <div className="space-y-2">
        <div className="flex items-center gap-2 rounded-lg border border-line/50 bg-surface-sunken px-2 py-2 sm:px-3 sm:py-2.5">
          <span className="min-w-0 flex-1 select-all break-all font-mono text-[11px] text-ink sm:text-xs">
            {url}
          </span>
          <button
            type="button"
            onClick={onCopy}
            className="flex size-8 shrink-0 items-center justify-center rounded-lg text-ink-muted transition-all hover:bg-surface-hover hover:text-ink active:scale-95"
            title="Copy to clipboard"
          >
            {copied ? (
              <Check className="size-4 text-success" strokeWidth={2.5} />
            ) : (
              <Copy className="size-4" strokeWidth={2} />
            )}
          </button>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onCopy}
            className="inline-flex items-center gap-1.5 rounded-lg bg-accent px-3 py-2 text-xs font-medium text-accent-ink shadow-sm transition-all hover:bg-accent-hover hover:shadow-md active:scale-[0.98] sm:py-1.5"
          >
            {copied ? (
              <>
                <Check className="size-3.5" strokeWidth={2.5} />
                Copied!
              </>
            ) : (
              <>
                <Copy className="size-3.5" strokeWidth={2} />
                Copy link
              </>
            )}
          </button>
        </div>
      </div>

      {/* Calendar app instructions */}
      <div className="rounded-xl border border-line/40 bg-surface-sunken/30">
        <div className="flex items-center gap-2 border-b border-line/30 px-3 py-2.5 sm:px-4">
          <ExternalLink className="size-3.5 text-ink-faint" strokeWidth={2} />
          <span className="text-[11px] font-medium text-ink-muted sm:text-xs">
            Where do I paste this?
          </span>
        </div>
        <div className="divide-y divide-line/20">
          {CALENDAR_APPS.map((app) => (
            <div key={app.name}>
              <button
                type="button"
                onClick={() => {
                  setExpandedApp(expandedApp === app.name ? null : app.name);
                }}
                className="flex w-full items-center gap-3 px-3 py-3 text-left transition-colors hover:bg-surface-hover/30 sm:px-4"
              >
                <span className="text-base">{app.icon}</span>
                <span className="flex-1 text-[11px] font-medium text-ink-muted sm:text-[12px]">
                  {app.name}
                </span>
                {expandedApp === app.name ? (
                  <ChevronDown className="size-4 text-ink-faint" />
                ) : (
                  <ChevronRight className="size-4 text-ink-faint" />
                )}
              </button>
              {expandedApp === app.name && (
                <div className="border-t border-line/20 bg-surface-sunken/50 px-3 py-3 sm:px-4">
                  <ol className="space-y-2">
                    {app.steps.map((step, i) => (
                      <li key={i} className="flex items-start gap-2.5">
                        <span className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full bg-accent/10 text-[10px] font-bold text-accent">
                          {i + 1}
                        </span>
                        <span className="text-[11px] leading-relaxed text-ink-faint">{step}</span>
                      </li>
                    ))}
                  </ol>
                </div>
              )}
            </div>
          ))}
        </div>
        <div className="flex items-center gap-2 border-t border-line/30 px-3 py-2 sm:px-4 sm:py-2.5">
          <Clock className="size-3 text-ink-faint" />
          <span className="text-[9px] text-ink-faint sm:text-[10px]">
            Calendar apps poll every 1-4 hours — synced cards appear within a few hours, not
            instantly
          </span>
        </div>
      </div>
    </div>
  );
}

/* ── Main component ────────────────────────────────────────────────── */

export function CalendarFeedSection() {
  const queryClient = useQueryClient();
  const { guard, dialog } = useStepUp();
  const [mintedUrl, setMintedUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const status = useQuery({
    queryKey: keys.calendarFeedStatus(),
    queryFn: () => api.auth.calendarFeed.status.query(),
  });

  const mint = useMutation({
    mutationFn: () => api.auth.calendarFeed.mint.mutate(),
    onSuccess: (result) => {
      setMintedUrl(result.url);
      setCopied(false);
      void queryClient.invalidateQueries({ queryKey: keys.calendarFeedStatus() });
    },
    onError: (error) => {
      guard(error, () => {
        mint.mutate();
      });
    },
  });

  const handleCopy = () => {
    if (mintedUrl) {
      void navigator.clipboard.writeText(mintedUrl).then(() => {
        setCopied(true);
      });
    }
  };

  return (
    <Section
      title="Calendar sync"
      description="See your synced cards in any calendar app. Generate a feed URL, add it to your calendar, then click the calendar icon on any card to sync it."
    >
      {status.isPending && <SkeletonRows rows={3} className="*:h-24" />}
      {status.isError && (
        <ErrorView error={status.error} title="Could not load your calendar feed status" />
      )}

      {status.data !== undefined && (
        <div className="space-y-4">
          {/* Status indicator */}
          <FeedStatus active={status.data.active} />

          {/* Mini calendar preview */}
          <MiniCalendarPreview />

          {/* How it works */}
          <HowItWorks />

          {/* Action button */}
          {mintedUrl === null && (
            <div className="space-y-2">
              <button
                type="button"
                disabled={mint.isPending}
                onClick={() => {
                  mint.mutate();
                }}
                className={`inline-flex w-full items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-semibold shadow-sm transition-all active:scale-[0.99] ${
                  status.data.active
                    ? 'bg-surface-raised text-ink border border-line/60 hover:bg-surface-hover hover:border-line-strong'
                    : 'bg-accent text-accent-ink shadow-[0_2px_8px_color-mix(in_oklab,var(--color-accent)_30%,transparent)] ring-1 ring-inset ring-white/10 hover:bg-accent-hover hover:shadow-[0_4px_12px_color-mix(in_oklab,var(--color-accent)_40%,transparent)]'
                } disabled:pointer-events-none disabled:opacity-50`}
              >
                {mint.isPending ? (
                  'Generating…'
                ) : status.data.active ? (
                  <>
                    <RefreshCw className="size-4" />
                    Regenerate feed URL
                  </>
                ) : (
                  <>
                    <Plus className="size-4" />
                    Generate calendar feed
                  </>
                )}
              </button>
              {status.data.active && (
                <p className="text-center text-[11px] text-ink-faint">
                  This will create a new link — the old one stops working immediately
                </p>
              )}
              {mint.isError && <ErrorText error={mint.error} />}
            </div>
          )}

          {/* URL reveal */}
          {mintedUrl !== null && <UrlReveal url={mintedUrl} onCopy={handleCopy} copied={copied} />}
        </div>
      )}

      {dialog}
    </Section>
  );
}
